import { TEST_IDS } from '@va/constants';
import { StatusIndicatorIcon } from '@va/icons';
import { useLocale } from '@va/localization';
import { fontWeights, Paragraph, paragraphSizes, ParagraphWithTooltip } from '@va/ui/design-system';
import { TooltipWrapper } from '@va/ui/tooltips';
import { addNumberSeparator } from '@va/util/helpers';
import { useIsHovering } from '@va/util/hooks';
import classNames from 'classnames';
import { ReactNode, useRef } from 'react';

type StatusTileProps = {
  label: string;
  icon?: ReactNode;
  value: number;
  disabled?: boolean;
  onClick?: () => void;
  actions?: ReactNode;
  className?: string;
  'data-testid'?: string;
  statusIndicator?: ReactNode;
  trendline?: ReactNode;
  valueTooltip?: string;
};

export const StatusTile = ({
  label,
  icon,
  value,
  disabled,
  onClick,
  actions,
  className,
  trendline,
  valueTooltip,
  'data-testid': dataTestId,
}: StatusTileProps) => {
  const { locale } = useLocale();
  const statusTileRef = useRef<HTMLDivElement>(null);
  const isHovering = useIsHovering(statusTileRef);
  const eventCount = disabled ? 'N/A' : addNumberSeparator(value, locale);

  return (
    <div
      ref={statusTileRef}
      onClick={onClick}
      className={classNames(
        'w-full h-10 py-3px pl-15px pr-3px flex items-center bg-white-snow hover:bg-white-smoke',
        className,
      )}
      data-testid={dataTestId}
    >
      <div className='flex items-center gap-2 w-full justify-between'>
        <div className='flex items-center gap-2 overflow-hidden'>
          {icon}
          <ParagraphWithTooltip
            data-testid={TEST_IDS.generic.primaryLabel}
            size={paragraphSizes.tiny}
            weight={fontWeights.medium}
            colorClassName='text-gray-charcoal'
          >
            {label}
          </ParagraphWithTooltip>
        </div>
        <div className='flex items-center gap-2 ml-auto'>
          <TooltipWrapper content={valueTooltip} disabled={!valueTooltip}>
            <Paragraph
              size={paragraphSizes.tiny}
              weight={fontWeights.medium}
              colorClassName={classNames({
                'text-gray-dustySolid': disabled,
              })}
              className='whitespace-nowrap'
            >
              {eventCount}
            </Paragraph>
          </TooltipWrapper>
          {!disabled &&
            (isHovering ? (
              actions
            ) : (
              <div className={'flex items-center gap-1'}>
                {trendline}
                <StatusIndicatorIcon isActive={+value === 0} />
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};
